import { FINISHED_STATUSES } from '../types/booking';
import type { BookingStatus, TripAttachment, TripDetail } from '../types/booking';

/**
 * Reading one finished trip.
 *
 * Every rule here is ported from the app's offer-status screen rather than
 * reinvented, because the same trip is described on both surfaces: if the web
 * decided that CAPTURED meant something else, or counted attachments
 * differently, a customer comparing the two would be right to distrust both.
 */

/**
 * Payment states that mean the customer has something to fix.
 *
 * The app checks both `paymentState` and `paymentFailureState` against this
 * set: the backend records the failure in the second field while the first may
 * still read as pending, so testing only one lets a dead card look like a card
 * that has not been charged yet.
 */
const FAILED_PAYMENT_STATES = new Set([
  'AUTH_FAILED',
  'CAPTURE_FAILED',
  'PAYMENT_RETRY_REQUIRED',
  'PAYMENT_CAPTURE_REQUIRED',
]);

/**
 * Where a trip is in its life, which is what decides what the page may offer.
 *
 * Three stages rather than a status switch, because only three distinctions
 * change the page: a finished trip has nothing to track, a scheduled one has no
 * driver to track yet, and a live one does. Everything else — LOADING versus
 * ENROUTE — is already said in words by the status chip.
 */
export type TripStage = 'finished' | 'scheduled' | 'live';

/**
 * DELIVERED is treated as over here even though `FINISHED_STATUSES` omits it.
 *
 * That list drives the history *query*, where the backend distinguishes a
 * delivered trip from a closed one. For "may this page offer live tracking",
 * delivered means the wagon has arrived and there is nothing left to watch —
 * and the app agrees: its Past lane lists DELIVERED alongside COMPLETED.
 */
const OVER: BookingStatus[] = [...FINISHED_STATUSES, 'DELIVERED'];

export function tripStage(trip: TripDetail): TripStage {
  if (OVER.includes(trip.status)) return 'finished';

  /* A trip booked for later has no driver assigned to follow yet, so offering
     to track it would land the customer on a map that reports the trip is not
     in progress — a dead end dressed as an action. */
  if (trip.scheduledAt) {
    const at = new Date(trip.scheduledAt).getTime();
    if (!Number.isNaN(at) && at > Date.now()) return 'scheduled';
  }

  return 'live';
}

export interface PaymentNotice {
  tone: 'go' | 'error' | 'info';
  message: string;
}

const normalisePaymentState = (state?: string | null) => String(state ?? '').trim().toUpperCase();

/** True when the trip is far enough out that the card has not been touched yet. */
function isMoreThanTwoHoursAway(iso?: string | null): boolean {
  if (!iso) return false;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return false;
  return at - Date.now() > 2 * 60 * 60 * 1000;
}

/**
 * What to say about this trip's money, or nothing.
 *
 * Nothing is the common case and is deliberately not padded out with "Payment
 * pending" — a banner that appears on every trip stops being read, and the fare
 * pill already says what the trip cost.
 */
export function paymentNotice(trip: TripDetail): PaymentNotice | null {
  const state = normalisePaymentState(trip.paymentState);
  const failure = normalisePaymentState(trip.paymentFailureState);

  if (FAILED_PAYMENT_STATES.has(state) || FAILED_PAYMENT_STATES.has(failure)) {
    return {
      tone: 'error',
      /* The app's copy ends "…before the driver can start loading", which is
         advice for a trip that has not run. This page is reached from history,
         so it names the state and leaves the fix to the app, which is the only
         place a card can actually be changed. */
      message: 'Payment did not go through. Update your card in the app to settle this trip.',
    };
  }

  if (state === 'AUTHORIZED') {
    return { tone: 'go', message: 'Funds reserved for this trip.' };
  }

  if (state === 'CAPTURED') {
    return { tone: 'go', message: 'Payment captured.' };
  }

  if (state === 'PAYMENT_METHOD_ATTACHED' && isMoreThanTwoHoursAway(trip.scheduledAt)) {
    return { tone: 'info', message: 'Your card will be authorised 2 hours before pickup.' };
  }

  return null;
}

/**
 * One list of attachments from the two the backend sends.
 *
 * `attachments` carries metadata; `attachmentUrls` is the older, bare-URL form,
 * and a trip can carry the same file in both. De-duplicating on the URL is what
 * the app does, and skipping it shows a customer three files when they uploaded
 * two — which reads as a bug in the upload, not in the list.
 */
export function normaliseAttachments(trip: TripDetail): TripAttachment[] {
  const byKey = new Map<string, TripAttachment>();
  const seenUrls = new Set<string>();

  for (const item of trip.attachments ?? []) {
    const url = item?.url;
    if (!url) continue;
    byKey.set(item.id || url, { ...item, id: item.id || url, url });
    seenUrls.add(url);
  }

  for (const url of trip.attachmentUrls ?? []) {
    if (!url || seenUrls.has(url)) continue;
    seenUrls.add(url);
    byKey.set(url, { id: url, url, originalName: attachmentNameFromUrl(url) });
  }

  return Array.from(byKey.values());
}

/** Best available name for a bare URL: its last path segment, query stripped. */
function attachmentNameFromUrl(url: string): string {
  const filename = url.split('/').pop()?.split('?')[0];
  if (!filename) return 'Attachment';

  try {
    return decodeURIComponent(filename);
  } catch {
    /* A malformed escape in a signed URL must not take the whole list down. */
    return filename;
  }
}

/** "PDF", "JPEG", or "FILE" when neither the name nor the type says. */
export function attachmentKind(attachment: TripAttachment): string {
  const fromName = attachment.originalName?.split('.').pop();
  /* Guard against a name with no dot: `split` returns the whole string, which
     would print the entire filename where a three-letter badge belongs. */
  if (fromName && fromName !== attachment.originalName) return fromName.toUpperCase();

  const fromType = attachment.contentType?.split('/').pop();
  return fromType ? fromType.toUpperCase() : 'FILE';
}

/** "4.1 km", or an em dash when the backend recorded no distance. */
export function formatDistanceKm(distanceKm?: number | null): string {
  if (distanceKm == null || !Number.isFinite(distanceKm)) return '—';
  return `${distanceKm.toFixed(1)} km`;
}

/** "7 min" / "1h 20m" — the app's `formatDuration`, ported. */
export function formatDurationMinutes(minutes?: number | null): string | null {
  if (minutes == null || !Number.isFinite(minutes)) return null;
  if (minutes < 60) return `${Math.round(minutes)} min`;

  const hours = Math.floor(minutes / 60);
  const remainder = Math.round(minutes % 60);
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
}

/**
 * Vehicle labels, matching the app's catalogue.
 *
 * Falling through to a de-underscored version rather than "-" keeps a vehicle
 * type the web has not heard of readable — new types ship in the backend before
 * they ship here, and the app's own fallback prints a dash for all of them.
 */
export function formatVehicleType(vehicleType?: string | null): string {
  if (!vehicleType) return '—';

  switch (vehicleType) {
    case 'BAKKIE':
      return 'Bakkie';
    case 'MEDIUM_TRUCK':
      return 'Medium truck';
    case 'SEMI_TRUCK':
      return 'Semi truck';
    case 'HORSE_TRAILER':
      return 'Horse trailer';
    default:
      return vehicleType.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
  }
}

/** The app's `fmtDateTime`: a scheduled trip says when, an instant one says ASAP. */
export function formatTripSchedule(iso?: string | null): string {
  if (!iso) return 'ASAP';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'ASAP';

  return `${date.toLocaleDateString('en-ZA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })} · ${date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`;
}
